import { useState } from 'react';

import {
    RowData,
    SortingState,
    columnVisibilityFeature,
    createColumnHelper,
    createSortedRowModel,
    flexRender,
    rowSortingFeature,
    sortFns,
    tableFeatures,
    useTable
} from '@tanstack/react-table';

import { useSiteSettingsContext } from '@core/shared/src/components/SiteSettingsProvider';
import { Loader } from './loader';

// import { Loader } from '.';

// v9 builds the table from an explicit feature set instead of shipping every feature.
// columnVisibilityFeature is not optional here: getVisibleCells() moved out of the core
// into it. The sorted row model has to be passed as well - stockFeatures looks like the
// shortcut but carries no row models, which type-checks while quietly not sorting.
export const tableFeatureSet = tableFeatures({
    rowSortingFeature,
    columnVisibilityFeature,
    sortedRowModel: createSortedRowModel(),
    sortFns
});

// The column helper is bound to that feature set, so callers keep passing just their row type.
export const useCreateColumnHelper = <TData extends RowData,>() => createColumnHelper<typeof tableFeatureSet, TData>();

export const Table = ({ data, columns, isHeader, isLoading, tableRowTestId }: any) => {
    const [sorting, setSorting] = useState<SortingState>([]);
    const isProduction = useSiteSettingsContext().isProduction;

    // manualPagination/pageCount went with the move: there is no paginated row model here
    // and the pagination footer below has been commented out for as long as this file exists.
    const table = useTable({
        features: tableFeatureSet,
        data,
        columns,
        state: {
            sorting
        },
        onSortingChange: setSorting,
        debugTable: !isProduction
    });
    return (
        <div data-testid="accountListTable">
            {isLoading && (
                <div className="p-5">
                    <Loader />
                </div>
            )}
            {!isLoading && (
                <div className="table-responsive">
                    <table className="table table-striped mt-2">
                        {isHeader && (
                            <thead>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => {
                                            return (
                                                <th key={header.id} colSpan={header.colSpan}>
                                                    {header.isPlaceholder ? null : (
                                                        <div
                                                            {...{
                                                                className: header.column.getCanSort() ? 'cursor-pointer' : '',
                                                                onClick: header.column.getToggleSortingHandler()
                                                            }}
                                                        >
                                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                                            {{
                                                                asc: ' 🔼',
                                                                desc: ' 🔽'
                                                            }[header.column.getIsSorted() as string] ?? null}
                                                        </div>
                                                    )}
                                                </th>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </thead>
                        )}
                        <tbody>
                            {table.getRowModel().rows.map((row) => (
                                <tr data-testid={tableRowTestId} key={row.id}>
                                    {row.getVisibleCells().map((cell) => (
                                        <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            {/* <div className='flex justify-around text-blue-600'>
            <button>{'<<'}</button>
            <button>{'<'}</button>
            <button>{'>'}</button>
            <button>{'>>'}</button>
          </div> */}
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    );
};
